import React, { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { saveAs } from "file-saver";
import { toBlob } from "html-to-image";
import { SlButton, SlIcon, SlTooltip } from "@shoelace-style/shoelace/dist/react";

import ScreenshotFrame from "./ScreenshotFrame";
import { Toast } from "../shared/Toast";
import { DEFAULT_BATTERY, makeDeviceStatus } from "../utils/deviceStatus";
import { getFrameFontCSS } from "../utils/frameFonts";
import { resolveImages, FrameImageError } from "../utils/frameImages";
import {
  buildFrames,
  detectFrameLanguage,
  frameLanguageOptions,
  isFrameable,
  toSafeFileName,
  CAPTURE_SCALE,
} from "../utils/screenshotFrame";

// html-to-image rejects with the browser's own Event, with no message, when the
// SVG it builds will not load. Say that, rather than nothing.
const describeError = (error) => {
  if (error instanceof Error && error.message) return error.message;
  if (error && error.type === "error") return "the browser could not build the preview image";
  return "";
};

// A capture that dies right after the SVG step has been seen to succeed when
// repeated straight away, so try twice. The first failure goes to the console.
const captureFrame = async (node, options) => {
  try {
    return await toBlob(node, options);
  } catch (firstError) {
    console.warn("Frame capture failed, retrying once", firstError);
    await new Promise((resolve) => setTimeout(resolve, 250));
    return toBlob(node, options);
  }
};

/**
 * Download the creative as a mobile article screenshot.
 *
 * The frames are kept mounted in an off screen host rather than mounted on
 * demand, so the browser has already decoded the images by the time the user
 * clicks download and html-to-image has something complete to serialise.
 */
function DownloadFramedPreview({ preview }) {
  const hostRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [language, setLanguage] = useState(() => detectFrameLanguage(preview));
  const [vary, setVary] = useState(true);
  const [battery, setBattery] = useState(DEFAULT_BATTERY);
  const [charging, setCharging] = useState(false);
  const [status, setStatus] = useState(() => makeDeviceStatus());

  const frames = useMemo(() => buildFrames(preview), [preview]);

  if (!isFrameable(preview) || !frames.length) return null;

  // html-to-image reads the DOM as it stands; a half decoded image serialises
  // blank, so wait for every image in the frame and for the Indic webfonts.
  const waitForAssets = async (node) => {
    const images = Array.from(node.querySelectorAll("img"));
    await Promise.all(
      images.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
              // A resource that neither loads nor errors would otherwise
              // leave the button spinning for good.
              setTimeout(resolve, 15000);
            })
      )
    );
    // complete only says the bytes arrived. decode() settles the image into
    // something html-to-image can draw straight away.
    await Promise.all(
      images.map((img) => (img.decode ? img.decode().catch(() => {}) : null))
    );
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
  };

  const handleDownload = async () => {
    const host = hostRef.current;
    if (!host) return;

    setDownloading(true);
    try {
      // The status bar shows the moment of capture, not the moment the drawer
      // opened, so it is drawn fresh here. flushSync commits it before the
      // nodes are read, without waiting on a frame a background tab may not give.
      flushSync(() =>
        setStatus(makeDeviceStatus({ vary, level: battery, charging }))
      );

      const nodes = Array.from(host.querySelectorAll(".ss-frame"));
      const baseName = toSafeFileName(preview.name);

      // Inline the creative images ourselves first, so html-to-image has no
      // cross origin resource left to fetch. Left to itself it swallows a
      // blocked fetch, substitutes an empty src and then fails the whole
      // capture with an event object carrying no message.
      const images = await resolveImages(frames.map((frame) => frame.imgUrl));

      // The YouTube thumbnail is derived, not something the creator supplied,
      // and can 404 for a private video. Drop it rather than fail the download.
      const thumbs = await resolveImages(
        frames.map((frame) => frame.videoThumb)
      ).catch(() => new Map());

      // Applied straight to the DOM rather than through state: this has to be
      // in place before the capture reads the node, and waiting on a React
      // commit would mean waiting on a frame, which a background tab may
      // never give us.
      nodes.forEach((node) => {
        node.querySelectorAll("img[data-src]").forEach((img) => {
          const dataUrl =
            images.get(img.dataset.src) || thumbs.get(img.dataset.src);
          if (dataUrl) img.src = dataUrl;
          else if (!images.has(img.dataset.src)) img.remove();
        });
      });

      // Built once for the whole batch. Passing fontEmbedCSS stops
      // html-to-image walking document.styleSheets and inlining every font
      // the app loads; see utils/frameFonts.js.
      const fontEmbedCSS = await getFrameFontCSS(language);

      for (let index = 0; index < nodes.length; index += 1) {
        const node = nodes[index];
        await waitForAssets(node);

        // No backgroundColor option on purpose: html-to-image writes it
        // straight onto the cloned root's inline style (apply-style.js), which
        // would paint over the frame's own background - white behind a full
        // screen ad that is meant to sit on black. Each frame declares its own.
        const blob = await captureFrame(node, {
          pixelRatio: CAPTURE_SCALE,
          width: node.offsetWidth,
          height: node.offsetHeight,
          fontEmbedCSS,
        });

        if (!blob) throw new Error("Could not render the preview");

        const suffix = nodes.length > 1 ? `_${index + 1}` : "";
        saveAs(blob, `${baseName}${suffix}.png`);
      }
    } catch (error) {
      console.error("Error @ handleDownload", error);
      if (error instanceof FrameImageError) {
        // The usual cause: the image host sends no Access-Control-Allow-Origin,
        // so the browser refuses to let us read the pixels. Name the host,
        // because that is where the fix belongs.
        console.error(`Blocked image URL: ${error.url}`, error.cause);
        Toast(
          "error",
          `${error.message}. That host has to allow cross-origin reads before the preview can be downloaded.`
        );
      } else {
        const reason = describeError(error);
        Toast(
          "error",
          `Could not download the creative preview${reason ? `: ${reason}` : ""}`
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="ss-frame-actions">
        <select
          value={language}
          onChange={(event) => setLanguage(event.target.value)}
          title="Language used for the frame's fonts"
          disabled={downloading}
        >
          {frameLanguageOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label
          className="ss-vary"
          title="Randomise battery, network and phone style, like screenshots from different phones"
        >
          <input
            type="checkbox"
            checked={vary}
            onChange={(event) => setVary(event.target.checked)}
            disabled={downloading}
          />
          Random phone look
        </label>

        <label className="ss-vary" title="Battery percentage shown in the status bar">
          Battery
          <input
            type="number"
            className="ss-battery-input"
            min={1}
            max={100}
            value={battery}
            onChange={(event) => setBattery(event.target.value)}
            disabled={downloading}
          />
          %
        </label>

        <label className="ss-vary" title="Show the charging bolt on the battery">
          <input
            type="checkbox"
            checked={charging}
            onChange={(event) => setCharging(event.target.checked)}
            disabled={downloading}
          />
          Charging
        </label>

        <SlTooltip
          content={
            frames.length > 1
              ? `Download ${frames.length} framed previews`
              : "Download framed preview"
          }
        >
          <SlButton
            size="small"
            variant="primary"
            loading={downloading}
            onClick={handleDownload}
          >
            <SlIcon library="lucide" name="download" slot="prefix" />
            Download Preview
          </SlButton>
        </SlTooltip>
      </div>

      <div className="ss-frame-capture-host" ref={hostRef} aria-hidden="true">
        {frames.map((frame, index) => (
          <ScreenshotFrame key={index} frame={frame} language={language} status={status} />
        ))}
      </div>
    </>
  );
}

export default DownloadFramedPreview;
